/**
 * Exception Event Detail Page
 * Shows exception event details and allows status transitions.
 * Handles version field for optimistic locking.
 * 
 * State machine: reported → technical_review → approved_action → executing → resolved → closed
 */
import { useEffect, useState } from 'react';
import { useParams } from 'wouter';
import { useExceptionEventDetail, useExceptionEventResolve, type ExceptionEventView } from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { canReadExceptionEvent, canUpdateExceptionEvent } from '@/lib/labPermissions';

type ExceptionEventStatus = 'reported' | 'technical_review' | 'approved_action' | 'executing' | 'resolved' | 'closed' | 'cancelled';
type ExceptionSeverity = 'low' | 'medium' | 'high' | 'critical';

// Valid state transitions per state-machines.md
// Exception Events: reported → technical_review → approved_action → executing → resolved → closed
const VALID_TRANSITIONS: Record<string, string[]> = {
  reported: ['technical_review', 'cancelled'],
  technical_review: ['approved_action', 'cancelled'],
  approved_action: ['executing', 'cancelled'],
  executing: ['resolved', 'cancelled'],
  resolved: ['closed'],
  closed: [],
  cancelled: [],
};

const SEVERITY_COLORS: Record<ExceptionSeverity, { bg: string; text: string; border: string }> = {
  critical: { bg: 'bg-red-50', text: 'text-red-900', border: 'border-red-200' },
  high: { bg: 'bg-orange-50', text: 'text-orange-900', border: 'border-orange-200' },
  medium: { bg: 'bg-yellow-50', text: 'text-yellow-900', border: 'border-yellow-200' },
  low: { bg: 'bg-blue-50', text: 'text-blue-900', border: 'border-blue-200' },
};

export default function ExceptionEventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { permissions } = useAuth();
  const { fetch: fetchDetail, loading: detailLoading } = useExceptionEventDetail(eventId!);
  const { resolve: resolveEvent, loading: resolveLoading } = useExceptionEventResolve(eventId!);

  const [event, setEvent] = useState<ExceptionEventView | null>(null);
  const [selectedTransition, setSelectedTransition] = useState<ExceptionEventStatus | ''>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Check permission — wildcard-aware (mirrors backend RBAC)
  if (!canReadExceptionEvent(permissions)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看异常事件的权限</p>
        </Card>
      </div>
    );
  }

  // Load exception event detail
  useEffect(() => {
    const loadEvent = async () => {
      try {
        const data = await fetchDetail();
        if (data) {
          setEvent(data);
        }
      } catch (err) {
        toast.error('加载异常事件详情失败');
      }
    };

    if (eventId) {
      loadEvent();
    }
  }, [eventId, fetchDetail]);

  const handleTransition = async () => {
    if (!event || !selectedTransition) {
      toast.error('请选择转换状态');
      return;
    }

    if (!canUpdateExceptionEvent(permissions)) {
      toast.error('您没有修改异常事件状态的权限');
      return;
    }

    try {
      await resolveEvent(selectedTransition, event.version ?? 0);
      toast.success('状态已更新');
      setSelectedTransition('');
      setConfirmDialogOpen(false);
      
      // Refetch event details to ensure consistency with backend
      const updatedData = await fetchDetail();
      if (updatedData) {
        setEvent(updatedData);
      }
    } catch (err: unknown) {
      // Backend rejection must be shown as-is
      toast.error(`状态转换失败: ${(err as Error)?.message || '未知错误'}`);
    }
  };

  if (detailLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">加载中…</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">异常事件不存在</h2>
        </Card>
      </div>
    );
  }

  const severity = (Object.keys(SEVERITY_COLORS).includes(event.severity) ? event.severity : 'low') as ExceptionSeverity;
  const severityColors = SEVERITY_COLORS[severity];
  const validTransitions = VALID_TRANSITIONS[event.status] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">异常事件详情</h1>
        <p className="text-sm text-muted-foreground">事件 ID: {event.id}</p>
      </div>

      {/* Severity Alert */}
      <Card className={`border-2 p-6 ${severityColors.border} ${severityColors.bg}`}>
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${severityColors.text}`}>
                {event.severity.toUpperCase()}
              </span>
              <h2 className={`text-xl font-semibold ${severityColors.text}`}>{event.category}</h2>
            </div>
            {event.description && (
              <p className="mt-2 text-sm">{event.description}</p>
            )}
          </div>
        </div>
      </Card>

      {/* Exception Event Info */}
      <Card className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-semibold text-muted-foreground">批次 ID</label>
            <p className="mt-1 text-sm font-mono">{event.cellBatchId}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">严重程度</label>
            <p className="mt-1 text-sm">
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${severityColors.text}`}>
                {event.severity.toUpperCase()}
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">当前状态</label>
            <p className="mt-1 text-sm">
              <span className="inline-block rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-900">
                {getStatusLabel(event.status)}
              </span>
            </p>
          </div>
          {event.version !== undefined && (
            <div>
              <label className="text-sm font-semibold text-muted-foreground">版本</label>
              <p className="mt-1 text-sm font-mono">{event.version}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-semibold text-muted-foreground">报告时间</label>
            <p className="mt-1 text-sm">{new Date(event.reportedAt).toLocaleString()}</p>
          </div>

          {event.resolvedAt && (
            <div>
              <label className="text-sm font-semibold text-muted-foreground">解决时间</label>
              <p className="mt-1 text-sm">{new Date(event.resolvedAt).toLocaleString()}</p>
            </div>
          )}
        </div>
      </Card>

      {/* Status Transition */}
      {validTransitions.length > 0 && canUpdateExceptionEvent(permissions) && (
        <Card className="p-6">
          <h3 className="mb-4 text-lg font-semibold">状态转换</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold">选择目标状态</label>
              <select
                value={selectedTransition}
                onChange={(e) => setSelectedTransition((e.target.value || '') as ExceptionEventStatus | '')}
                className="mt-2 w-full rounded border border-input bg-background px-3 py-2 text-sm"
                disabled={resolveLoading}
              >
                <option value="">-- 选择状态 --</option>
                {validTransitions.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => setConfirmDialogOpen(true)}
              disabled={!selectedTransition || resolveLoading}
              className="w-full"
            >
              {resolveLoading ? '处理中…' : '确认转换'}
            </Button>
          </div>
        </Card>
      )}

      {/* Confirmation Dialog (simple version) */}
      {confirmDialogOpen && (
        <Card className="border-yellow-200 bg-yellow-50 p-6">
          <h4 className="font-semibold text-yellow-900">确认状态转换</h4>
          <p className="mt-2 text-sm text-yellow-800">
            您确定要将异常事件从 <strong>{getStatusLabel(event.status)}</strong> 转换到{' '}
            <strong>{getStatusLabel(selectedTransition)}</strong> 吗？
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={handleTransition}
              disabled={resolveLoading}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {resolveLoading ? '处理中…' : '确认'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                setSelectedTransition('');
              }}
              disabled={resolveLoading}
            >
              取消
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    reported: '已报告',
    technical_review: '技术审查中',
    approved_action: '已批准处理',
    executing: '执行中',
    resolved: '已解决',
    closed: '已关闭',
    cancelled: '已取消',
  };
  return labels[status] || status;
}
